import { EditorContent, useEditor } from '@tiptap/react';
import { useUploadBlogImage } from '@/lib/api/hooks/use-blogs';
import { defaultExtensions } from './editor-extensions';
import { EditorBubbleMenu } from './editor-bubble-menu';
import { EditorContextMenu } from './editor-context-menu';
import { ImageUploadDialog } from './image-upload-dialog';
import { useEffect, useState } from 'react';

interface BlogEditorProps {
  initialContent?: string;
  onChange: (content: string) => void;
}

export function BlogEditor({ initialContent, onChange }: BlogEditorProps) {
  const uploadImage = useUploadBlogImage();
  const [showImageDialog, setShowImageDialog] = useState(false);

  const editor = useEditor({
    extensions: defaultExtensions,
    content: initialContent,
    editorProps: {
      attributes: {
        class: 'prose prose-xl dark:prose-invert focus:outline-none max-w-none font-serif leading-relaxed',
        style: 'font-size: 21px; line-height: 32px;',
      },
    },
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  useEffect(() => {
    if (editor && initialContent && editor.getHTML() !== initialContent) {
      editor.commands.setContent(initialContent);
    }
  }, [initialContent, editor]);

  if (!editor) {
    return null;
  }

  const handleImageInsert = (src: string) => {
    editor?.chain().focus().setImage({
      src: src,
      alt: 'Blog image',
      class: 'w-full h-auto rounded-lg my-6 block mx-auto',
      style: 'max-width: 100%; height: auto; display: block; margin-left: auto; margin-right: auto;'
    }).run();
    setShowImageDialog(false);
  };

  return (
    <div className="relative w-full min-h-[600px]">
      <EditorBubbleMenu editor={editor} />
      <EditorContextMenu 
        editor={editor} 
        onOpenImageDialog={() => {
          setShowImageDialog(true);
        }}
      />
      <div
        className="min-h-[600px] cursor-text"
        onClick={() => editor?.chain().focus().run()}
      >
        <EditorContent
          editor={editor}
          className="prose prose-xl dark:prose-invert max-w-none min-h-[600px] focus:outline-none"
        />
      </div>
      {uploadImage.isPending && (
        <div className="absolute -top-12 right-0 bg-primary text-primary-foreground px-3 py-1.5 rounded-md text-xs">
          Uploading image...
        </div>
      )}
      
      {/* Image Upload Dialog */}
      <ImageUploadDialog
        isOpen={showImageDialog}
        onClose={() => setShowImageDialog(false)}
        onImageInsert={handleImageInsert}
      />
    </div>
  );
}
